import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CaretRight, Dna, Flask, Microscope } from '@phosphor-icons/react';
import type { Signature } from '../../services/types';
import { getTissueImage } from '../../utils/tissueImageMapping';

interface SignatureFlipCardProps {
  signature: Signature;
  isSelected: boolean;
  onSelect: (signatureId: string) => void;
}

export function SignatureFlipCard({
  signature,
  isSelected,
  onSelect
}: SignatureFlipCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);

  // Auto-flip back when another signature is selected
  useEffect(() => {
    if (!isSelected && isFlipped) {
      setIsFlipped(false);
    }
    // isFlipped intentionally excluded: this effect should only react to the
    // card losing selection, not to the user manually flipping it — including
    // isFlipped would immediately un-flip the card the instant the user flips
    // it manually while unselected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);
  
  const imageUrl = getTissueImage(signature.tissue_type);

  const handleToggleFlip = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFlipped(!isFlipped);
    if (!isSelected) {
      onSelect(signature.id);
    }
  };

  return (
    <motion.div
      className="relative h-80 cursor-pointer group"
      onClick={handleToggleFlip}
      style={{ perspective: '1200px' }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      {/* Selection ring with glow effect */}
      <motion.div
        className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-primary via-primary/80 to-primary opacity-0 blur-sm"
        animate={{ opacity: isSelected ? 0.6 : 0 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Flip container */}
      <motion.div
        className="relative w-full h-full"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ 
          duration: 0.6, 
          ease: [0.23, 1, 0.32, 1] // Custom ease for smooth flip
        }}
      >
        {/* Front of card */}
        <div
          className={`absolute w-full h-full rounded-2xl overflow-hidden border-2 transition-colors duration-300 ${
            isSelected 
              ? 'border-primary shadow-lg shadow-primary/20' 
              : 'border-border hover:border-primary/50'
          }`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {/* Image Container with gradient overlay */}
          <div className="relative h-3/5 overflow-hidden">
            <img
              src={imageUrl}
              alt={signature.tissue_type || 'Signature'}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  'https://images.unsplash.com/photo-1557672186-269926e526c1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiaW9sb2d5fGVufDF8fHx8fDE3Njc5NTI3OTJ8MA&ixlib=rb-4.1.0&q=80&w=1080';
              }}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
            
            {/* Floating badge for tissue type */}
            {signature.tissue_type && (
              <motion.div
                className="absolute top-3 left-3 px-3 py-1 bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs font-medium rounded-full shadow-lg"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {signature.tissue_type}
              </motion.div>
            )}
            
            {/* Selection indicator */}
            {isSelected && (
              <motion.div
                className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-lg"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              >
                <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
            )}
          </div>

          {/* Info section */}
          <div className="h-2/5 p-4 bg-card flex flex-col justify-between">
            <div>
              <h4 className="font-semibold text-foreground text-base truncate mb-1">
                {signature.name}
              </h4>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                {signature.cell_type_count && (
                  <span className="flex items-center gap-1">
                    <Flask className="w-3 h-3" />
                    {signature.cell_type_count} cell types
                  </span>
                )}
                {signature.gene_count && (
                  <span className="flex items-center gap-1">
                    <Dna className="w-3 h-3" />
                    {signature.gene_count.toLocaleString()} genes
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-2 border-t border-border/50">
              <span className="text-xs text-muted-foreground">Click to see details</span>
              <motion.div
                animate={{ x: [0, 4, 0] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
              >
                <CaretRight className="w-4 h-4 text-primary" />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Back of card */}
        <div
          className={`absolute w-full h-full rounded-2xl overflow-hidden border-2 transition-colors duration-300 bg-card ${
            isSelected 
              ? 'border-primary shadow-lg shadow-primary/20' 
              : 'border-border'
          }`}
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          {/* Subtle background image (no mirrored text/icons) */}
          <img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-15 grayscale"
          />
          {/* Contrast overlays */}
          <div className="absolute inset-0 bg-gradient-to-br from-background/80 via-background/90 to-background/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-background/70" />
          
          {/* Content */}
          <div className="relative h-full p-5 flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border/50">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Microscope className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-foreground text-sm truncate">
                  {signature.name}
                </h4>
                {signature.tissue_type && (
                  <p className="text-xs text-muted-foreground">{signature.tissue_type}</p>
                )}
              </div>
            </div>
            
            {/* Stats grid */}
            <div className="flex-1 grid grid-cols-2 gap-3">
              {signature.cell_type_count && (
                <motion.div
                  className="p-3 bg-background/95 backdrop-blur-sm rounded-xl border border-border/60"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: isFlipped ? 1 : 0, y: isFlipped ? 0 : 10 }}
                  transition={{ delay: 0.1 }}
                >
                  <p className="text-lg font-bold text-primary mb-1 leading-tight tracking-tight">
                    {signature.cell_type_count}
                  </p>
                  <p className="text-xs text-muted-foreground">Cell Types</p>
                </motion.div>
              )}

              {signature.gene_count && (
                <motion.div
                  className="p-3 bg-background/95 backdrop-blur-sm rounded-xl border border-border/60"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: isFlipped ? 1 : 0, y: isFlipped ? 0 : 10 }}
                  transition={{ delay: 0.2 }}
                >
                  <p className="text-lg font-bold text-primary mb-1 leading-tight tracking-tight">
                    {signature.gene_count.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">Genes</p>
                </motion.div>
              )}

              {signature.organism && (
                <motion.div
                  className="col-span-2 p-3 bg-background/95 backdrop-blur-sm rounded-xl border border-border/60"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: isFlipped ? 1 : 0, y: isFlipped ? 0 : 10 }}
                  transition={{ delay: 0.3 }}
                >
                  <p className="text-sm font-medium text-foreground capitalize">
                    {signature.organism}
                  </p>
                  <p className="text-xs text-muted-foreground">Organism</p>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-border/50 flex items-center justify-center">
              <motion.span 
                className="text-xs text-primary font-medium"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                Click to flip back
              </motion.span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
